import { atom, selector } from 'recoil';
import { Collection, Organization } from '@givz/core-client';
import { OrganizationsData, OrganizationWithId } from '../../types';
import { selectedCampaignSelector } from '../campaign';

export const donateOnExpirationOrganizationState = atom<
  Organization | undefined
>({
  default: undefined,
  key: 'donateOnExpirationOrganizationState',
});

export const givzFeaturedOrganizationsState = atom<Collection | undefined>({
  default: undefined,
  key: 'givzFeaturedOrganizationsState',
});

export const unsavedOrganizationsState = atom<Organization[]>({
  default: [],
  key: 'unsavedOrganizationsState',
});

export const organizationsSearchState = atom<string>({
  default: '',
  key: 'organizationsSearchState',
});

export const organizationsSearchResultsState = atom<Organization[]>({
  default: [],
  key: 'organizationsSearchResultsState',
});

export const organizationsLoadingState = atom<boolean>({
  default: false,
  key: 'organizationsLoadingState',
});

export const organizationsDataSelector = selector<OrganizationsData>({
  get: ({ get }) => {
    const givzFeaturedOrganizations = get(givzFeaturedOrganizationsState);
    const donateOnExpirationOrganization = get(
      donateOnExpirationOrganizationState,
    );
    const selectedCampaign = get(selectedCampaignSelector);
    const organizationsSearchResults = get(organizationsSearchResultsState);
    const unsavedOrganizations = get(unsavedOrganizationsState);

    const organizationsData: OrganizationsData = {};

    if (givzFeaturedOrganizations?.organizations) {
      transformOrganizationsToOrganizationsData(
        givzFeaturedOrganizations?.organizations,
        organizationsData,
      );
    }

    if (selectedCampaign?.collection) {
      transformOrganizationsToOrganizationsData(
        selectedCampaign?.collection.organizations,
        organizationsData,
      );
    }

    if (donateOnExpirationOrganization) {
      transformOrganizationsToOrganizationsData(
        [donateOnExpirationOrganization],
        organizationsData,
      );
    }

    transformOrganizationsToOrganizationsData(
      [...organizationsSearchResults, ...unsavedOrganizations],
      organizationsData,
    );

    return organizationsData;
  },
  key: 'organizationsDataSelector',
});

export const givzFeaturedOrganizationsSelector = selector<string[]>({
  get: ({ get }) => {
    const givzFeaturedOrganizations = get(givzFeaturedOrganizationsState);

    return (
      givzFeaturedOrganizations?.organizations.map(
        (organization) => organization?.gid,
      ) ?? []
    );
  },
  key: 'givzFeaturedOrganizationsSelector',
});

export const organizationsOptionsSelector = selector<OrganizationWithId[]>({
  get: ({ get }) => {
    const organizationsSearchResults = get(organizationsSearchResultsState);

    return organizationsSearchResults.map((organization) => {
      return { ...organization, id: organization.gid };
    });
  },
  key: 'organizationsOptionsSelector',
});

const transformOrganizationsToOrganizationsData = (
  organizations: Organization[],
  organizationsData: OrganizationsData,
) => {
  organizations.forEach((organization) => {
    organizationsData[organization.gid] = organization;
  });
};
